import type { ThemePresetId, ThemeTokens } from '@/types'

const radius = { sm: '4px', md: '8px', lg: '12px', xl: '16px' }
const shadowsLight = {
  sm: '0 1px 2px rgba(0,0,0,0.04)',
  md: '0 4px 12px rgba(0,0,0,0.08)',
  lg: '0 8px 24px rgba(0,0,0,0.12)',
  xl: '0 16px 48px rgba(0,0,0,0.16)',
}
const shadowsDark = {
  sm: '0 1px 2px rgba(0,0,0,0.2)',
  md: '0 4px 12px rgba(0,0,0,0.3)',
  lg: '0 8px 24px rgba(0,0,0,0.4)',
  xl: '0 16px 48px rgba(0,0,0,0.5)',
}

export const THEME_PRESETS: Record<
  ThemePresetId,
  { light: ThemeTokens; dark: ThemeTokens; label: string; description: string }
> = {
  'modern-saas': {
    label: 'Modern SaaS',
    description: 'Clean, minimal design with blue accents',
    light: {
      colors: {
        bg: '#ffffff',
        bgSecondary: '#f5f5f7',
        bgTertiary: '#e8e8ed',
        bgHover: 'rgba(0,0,0,0.04)',
        bgActive: 'rgba(0,0,0,0.08)',
        text: '#1d1d1f',
        textSecondary: '#6e6e73',
        textTertiary: '#aeaeb2',
        border: '#d2d2d7',
        borderLight: '#e5e5ea',
        accent: '#0071e3',
        accentHover: '#0077ed',
        accentLight: 'rgba(0,113,227,0.1)',
        success: '#30d158',
        warning: '#ff9f0a',
        error: '#ff453a',
        info: '#64d2ff',
      },
      radius,
      shadows: shadowsLight,
    },
    dark: {
      colors: {
        bg: '#0f0f23',
        bgSecondary: '#1a1a2e',
        bgTertiary: '#252540',
        bgHover: 'rgba(255,255,255,0.06)',
        bgActive: 'rgba(255,255,255,0.1)',
        text: '#e5e5ea',
        textSecondary: '#98989d',
        textTertiary: '#636366',
        border: '#38383a',
        borderLight: '#2c2c2e',
        accent: '#0a84ff',
        accentHover: '#409cff',
        accentLight: 'rgba(10,132,255,0.15)',
        success: '#30d158',
        warning: '#ff9f0a',
        error: '#ff453a',
        info: '#64d2ff',
      },
      radius,
      shadows: shadowsDark,
    },
  },

  'bold-vibrant': {
    label: 'Bold & Vibrant',
    description: 'High saturation colors with vivid accents',
    light: {
      colors: {
        bg: '#faf9ff',
        bgSecondary: '#f0edff',
        bgTertiary: '#e4e0f5',
        bgHover: 'rgba(124,58,237,0.06)',
        bgActive: 'rgba(124,58,237,0.12)',
        text: '#1a1033',
        textSecondary: '#6b5f80',
        textTertiary: '#a89bc2',
        border: '#d4c8ed',
        borderLight: '#e8e0f7',
        accent: '#7c3aed',
        accentHover: '#6d28d9',
        accentLight: 'rgba(124,58,237,0.1)',
        success: '#10b981',
        warning: '#f59e0b',
        error: '#ef4444',
        info: '#06b6d4',
      },
      radius: { sm: '6px', md: '10px', lg: '14px', xl: '20px' },
      shadows: {
        sm: '0 1px 3px rgba(124,58,237,0.06)',
        md: '0 4px 16px rgba(124,58,237,0.1)',
        lg: '0 8px 32px rgba(124,58,237,0.14)',
        xl: '0 16px 56px rgba(124,58,237,0.18)',
      },
    },
    dark: {
      colors: {
        bg: '#0c0a1a',
        bgSecondary: '#16132d',
        bgTertiary: '#201c3e',
        bgHover: 'rgba(224,64,251,0.08)',
        bgActive: 'rgba(224,64,251,0.14)',
        text: '#f0eaff',
        textSecondary: '#a899cc',
        textTertiary: '#6b5f8a',
        border: '#2e2850',
        borderLight: '#231f42',
        accent: '#e040fb',
        accentHover: '#d946ef',
        accentLight: 'rgba(224,64,251,0.15)',
        success: '#34d399',
        warning: '#fbbf24',
        error: '#f87171',
        info: '#22d3ee',
      },
      radius: { sm: '6px', md: '10px', lg: '14px', xl: '20px' },
      shadows: {
        sm: '0 1px 3px rgba(224,64,251,0.1)',
        md: '0 4px 16px rgba(224,64,251,0.15)',
        lg: '0 8px 32px rgba(224,64,251,0.2)',
        xl: '0 16px 56px rgba(224,64,251,0.25)',
      },
    },
  },

  'glass-blur': {
    label: 'Glass & Blur',
    description: 'Frosted glass panels with backdrop blur',
    light: {
      colors: {
        bg: '#f2f2f7',
        bgSecondary: 'rgba(255,255,255,0.72)',
        bgTertiary: 'rgba(255,255,255,0.5)',
        bgHover: 'rgba(0,0,0,0.04)',
        bgActive: 'rgba(0,0,0,0.08)',
        text: '#1d1d1f',
        textSecondary: '#6e6e73',
        textTertiary: '#aeaeb2',
        border: 'rgba(0,0,0,0.08)',
        borderLight: 'rgba(0,0,0,0.04)',
        accent: '#007aff',
        accentHover: '#0066d6',
        accentLight: 'rgba(0,122,255,0.1)',
        success: '#34c759',
        warning: '#ff9500',
        error: '#ff3b30',
        info: '#5ac8fa',
      },
      radius: { sm: '8px', md: '12px', lg: '16px', xl: '20px' },
      shadows: {
        sm: '0 1px 2px rgba(0,0,0,0.03)',
        md: '0 4px 16px rgba(0,0,0,0.06)',
        lg: '0 8px 32px rgba(0,0,0,0.08)',
        xl: '0 16px 48px rgba(0,0,0,0.1)',
      },
      glass: { enabled: true, blur: '20px', backgroundOpacity: 0.72, borderOpacity: 0.08 },
    },
    dark: {
      colors: {
        bg: '#1c1c1e',
        bgSecondary: 'rgba(44,44,46,0.72)',
        bgTertiary: 'rgba(58,58,60,0.5)',
        bgHover: 'rgba(255,255,255,0.06)',
        bgActive: 'rgba(255,255,255,0.1)',
        text: '#f5f5f7',
        textSecondary: '#a1a1a6',
        textTertiary: '#636366',
        border: 'rgba(255,255,255,0.08)',
        borderLight: 'rgba(255,255,255,0.04)',
        accent: '#0a84ff',
        accentHover: '#409cff',
        accentLight: 'rgba(10,132,255,0.15)',
        success: '#30d158',
        warning: '#ff9f0a',
        error: '#ff453a',
        info: '#64d2ff',
      },
      radius: { sm: '8px', md: '12px', lg: '16px', xl: '20px' },
      shadows: {
        sm: '0 1px 2px rgba(0,0,0,0.1)',
        md: '0 4px 16px rgba(0,0,0,0.15)',
        lg: '0 8px 32px rgba(0,0,0,0.2)',
        xl: '0 16px 48px rgba(0,0,0,0.25)',
      },
      glass: { enabled: true, blur: '20px', backgroundOpacity: 0.72, borderOpacity: 0.08 },
    },
  },

  'dark-pro': {
    label: 'Dark Pro',
    description: 'IDE-inspired with warm amber accents',
    light: {
      colors: {
        bg: '#f0ede8',
        bgSecondary: '#e8e4de',
        bgTertiary: '#ddd8d0',
        bgHover: 'rgba(0,0,0,0.05)',
        bgActive: 'rgba(0,0,0,0.1)',
        text: '#2c2825',
        textSecondary: '#7a746c',
        textTertiary: '#a9a298',
        border: '#c8c1b7',
        borderLight: '#ddd8d0',
        accent: '#0d9488',
        accentHover: '#0f766e',
        accentLight: 'rgba(13,148,136,0.1)',
        success: '#16a34a',
        warning: '#ca8a04',
        error: '#dc2626',
        info: '#0891b2',
      },
      radius: { sm: '3px', md: '6px', lg: '8px', xl: '12px' },
      shadows: {
        sm: '0 1px 2px rgba(0,0,0,0.05)',
        md: '0 2px 8px rgba(0,0,0,0.08)',
        lg: '0 4px 16px rgba(0,0,0,0.1)',
        xl: '0 8px 32px rgba(0,0,0,0.14)',
      },
    },
    dark: {
      colors: {
        bg: '#111111',
        bgSecondary: '#1a1a1a',
        bgTertiary: '#242424',
        bgHover: 'rgba(255,255,255,0.05)',
        bgActive: 'rgba(255,255,255,0.08)',
        text: '#e8e4de',
        textSecondary: '#9c9790',
        textTertiary: '#6b6660',
        border: '#333333',
        borderLight: '#262626',
        accent: '#f59e0b',
        accentHover: '#fbbf24',
        accentLight: 'rgba(245,158,11,0.12)',
        success: '#22c55e',
        warning: '#eab308',
        error: '#ef4444',
        info: '#06b6d4',
      },
      radius: { sm: '3px', md: '6px', lg: '8px', xl: '12px' },
      shadows: {
        sm: '0 1px 2px rgba(0,0,0,0.2)',
        md: '0 2px 8px rgba(0,0,0,0.3)',
        lg: '0 4px 16px rgba(0,0,0,0.4)',
        xl: '0 8px 32px rgba(0,0,0,0.5)',
      },
    },
  },
}

/** Get a specific preset variant */
export function getPresetTokens(presetId: ThemePresetId, mode: 'light' | 'dark'): ThemeTokens {
  return THEME_PRESETS[presetId][mode]
}
